import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useScrollLock, useScrollTo } from '../layout/SmoothScroll';
import { pickQuality, supportsWebGL2 } from './engine/support';
import { createAmbience } from './engine/audio';
import { LAST_CHAPTER, WORLDS } from './constants';
import { labWorldBySlug } from '../../data/lab';
import LabGate from './LabGate';
import LabChapters from './LabChapters';
import LabHud from './LabHud';
import LabWorld from './LabWorld';
import { useChapterSteps } from './useChapterSteps';

const SEGMENT_VH = 130; // scroll distance per chapter
const ENTERED_KEY = 'dvs-lab-entered';

const storage = {
  get: key => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Private mode: the gate shows again next time, which is harmless
    }
  }
};

// Without WebGL2 (or if the engine fails) the copy still works over a still backdrop
const Backdrop = () => (
  <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_65%_40%,hsl(var(--brand)/0.22),transparent_70%)]">
    <div className="blueprint absolute inset-0 opacity-50" />
  </div>
);

/*
 * The landing page's immersive opening (Active Theory-leaning). A tall section pins a
 * full-screen stage (canvas, copy, HUD) while the scroll position (0…5, one unit per chapter)
 * drives the WebGL engine: the particles morph from one chapter's formation into the next
 * and the colour script and camera blend between them. Entering a world (?world=slug, so
 * Back works) dives the camera in. The engine and three.js load as their own chunk: behind
 * the gate on a first visit; on a return visit the copy and the scroll work at once and the
 * field flies in when it's built.
 */
const LabExperience = () => {
  const reduced = useReducedMotion();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const scrollTo = useScrollTo();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const audioRef = useRef(null);
  const audioView = useRef(0);
  const pushedWorld = useRef(false);
  const wasInWorld = useRef(false);
  const [webgl] = useState(supportsWebGL2);
  const [quality] = useState(() => params.get('quality') || pickQuality());
  const [engineState, setEngineState] = useState(webgl ? 'loading' : 'failed');
  const [entered, setEntered] = useState(() => storage.get(ENTERED_KEY) === '1');
  const [loaded, setLoaded] = useState(0);
  const [sound, setSound] = useState(false);
  const [chapter, setChapter] = useState(0);
  const [preview, setPreviewSlug] = useState(null);
  const [worldOpen, setWorldOpen] = useState(false);
  const world = WORLDS[params.get('world')] ? params.get('world') : null;
  const ready = engineState === 'ready';
  // The loading screen shows once per session, until the field is built (never without WebGL)
  const gate = !entered && engineState !== 'failed';

  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] });
  const progress = useTransform(scrollYProgress, v => v * LAST_CHAPTER);

  useEffect(() => {
    if (!webgl) return undefined;
    let cancelled = false;
    let engine = null;
    setEngineState('loading');
    import('./engine/engine')
      .then(({ createLabEngine }) => {
        if (cancelled) return null;
        engine = createLabEngine(canvasRef.current, { quality, reduced: !!reduced, adaptive: !params.get('quality') });
        engineRef.current = engine;
        return engine.init(p => !cancelled && setLoaded(0.1 + p * 0.9));
      })
      .then(() => {
        if (cancelled || !engine) return;
        engine.setProgress(progress.get());
        engine.start();
        setEngineState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        engine?.destroy();
        engineRef.current = null;
        setEngineState('failed');
      });
    return () => {
      cancelled = true;
      engine?.destroy();
      engineRef.current = null;
    };
  }, [webgl, quality, reduced]);

  useMotionValueEvent(progress, 'change', r => {
    engineRef.current?.setProgress(r);
    audioRef.current?.setMotion(Math.abs(progress.getVelocity()));
    const k = Math.round(r);
    setChapter(c => (c === k ? c : k));
  });

  // Pointer anywhere over the page steers the field (normalised to the viewport)
  useEffect(() => {
    const move = e => engineRef.current?.setPointer((e.clientX / window.innerWidth) * 2 - 1, -((e.clientY / window.innerHeight) * 2 - 1));
    const resize = () => engineRef.current?.resize();
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // Render and play only while the stage is on screen and the tab is visible
  useEffect(() => {
    let onScreen = true;
    const sync = () => {
      const show = onScreen && document.visibilityState === 'visible';
      if (ready && show) engineRef.current?.start();
      else if (ready) engineRef.current?.stop();
      audioRef.current?.setVisible(show);
    };
    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      sync();
    });
    observer.observe(containerRef.current);
    document.addEventListener('visibilitychange', sync);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, [ready]);

  // Hold the page still behind the gate and inside a world
  useScrollLock(gate || !!world);

  // One scroll gesture moves exactly one chapter
  useChapterSteps(containerRef, progress, !reduced && !gate && !world);

  const toggleSound = useCallback(next => {
    const want = typeof next === 'boolean' ? next : !audioRef.current?.on;
    if (want && !audioRef.current) audioRef.current = createAmbience(audioView.current);
    if (want) audioRef.current?.start();
    else audioRef.current?.stop();
    setSound(want && !!audioRef.current);
  }, []);

  useEffect(() => () => audioRef.current?.destroy(), []);

  // The score follows the chapter, the hovered world or the world we're in
  useEffect(() => {
    audioView.current = world ? WORLDS[world] : preview ? WORLDS[preview] : chapter;
    audioRef.current?.setChapter(audioView.current);
  }, [chapter, preview, world]);

  // Once the field is built the loading screen lifts by itself, a beat after it reads 100
  useEffect(() => {
    if (!ready || entered) return undefined;
    const id = setTimeout(() => {
      storage.set(ENTERED_KEY, '1');
      setEntered(true);
    }, 700);
    return () => clearTimeout(id);
  }, [ready, entered]);

  // The field assembles once it's built and the visitor is in
  useEffect(() => {
    if (ready && entered) engineRef.current?.intro();
  }, [ready, entered]);

  // Diving in and surfacing follow the ?world= parameter, so the browser's Back button works
  useEffect(() => {
    const engine = engineRef.current;
    if (engineState === 'failed') {
      setWorldOpen(!!world);
      return;
    }
    if (!ready || !entered || !engine) return;
    if (world) {
      wasInWorld.current = true;
      setWorldOpen(false);
      audioRef.current?.whoosh(true);
      engine.enterWorld(WORLDS[world]).then(() => setWorldOpen(true));
    } else if (wasInWorld.current) {
      wasInWorld.current = false;
      setWorldOpen(false);
      engine.exitWorld();
      audioRef.current?.whoosh(false);
    }
  }, [world, ready, entered, engineState]);

  const openWorld = slug => {
    pushedWorld.current = true;
    setParams({ world: slug });
  };

  const closeWorld = useCallback(() => {
    if (pushedWorld.current) {
      pushedWorld.current = false;
      navigate(-1);
    } else setParams({}, { replace: true });
  }, [navigate, setParams]);

  const setPreview = slug => {
    if (slug && slug !== preview) audioRef.current?.hover(WORLDS[slug]);
    setPreviewSlug(slug);
    engineRef.current?.setPreview(slug ? WORLDS[slug] : null);
  };

  const goTo = useCallback(
    k => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      scrollTo(top + (k / LAST_CHAPTER) * (el.offsetHeight - window.innerHeight));
    },
    [scrollTo]
  );

  return (
    <section ref={containerRef} aria-label="Dreamvision worlds" data-tone="ink" className="theme-ink relative bg-background" style={{ height: `${LAST_CHAPTER * SEGMENT_VH + 100}vh` }}>
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        {engineState !== 'failed' ? <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 block size-full" /> : <Backdrop />}

        <LabChapters progress={progress} reduced={!!reduced} hidden={gate || !!world} goTo={goTo} onOpen={openWorld} onPreview={setPreview} />
        <LabHud chapter={chapter} progress={progress} goTo={goTo} sound={sound} onSound={toggleSound} hidden={gate} />

        <AnimatePresence>{worldOpen && world && <LabWorld key={world} world={labWorldBySlug[world]} onClose={closeWorld} />}</AnimatePresence>
        <AnimatePresence>{gate && <LabGate key="gate" phase={ready ? 'gate' : 'loading'} loaded={loaded} />}</AnimatePresence>
      </div>
    </section>
  );
};

export default LabExperience;
